import { createApi } from "@reduxjs/toolkit/query/react";

import { getDynamicApiUrl } from "utils/api-config.util";
import { baseQueryWithReauthFn } from "utils/services.util";

export const API_TAGS = [
  "DialstackCallMetrics",
  // The user's desk phones ride on the caller-ids payload; logging in on one
  // changes them (ENG-2293).
  "DialstackCallerIds",
  "DialstackDeskPhones",
  "DialstackCalls",
  "DialstackClinicRouting",
  "DialstackContacts",
  "DialstackPatientCalls",
  "DialstackRecordingSettings",
  "DialstackSetup",
  "DialstackPortOrders",
  "DialstackVoiceAudioClips",
  // ENG-1946 calendar-aware closures. Two tags: rules/config changes alter how
  // upcoming events resolve, so those mutations invalidate BOTH; a per-event
  // override only touches the events list.
  "DialstackVoiceClosures",
  "DialstackVoiceClosureEvents",
  "DialstackVoiceDialPlans",
  "DialstackVoiceRingGroups",
  "DialstackVoiceSchedules",
  "DialstackVoicemailGreeting",
  "DialstackVoicemailUnreadCount",
  // ENG-2490: the shared voicemail boxes the signed-in user may read.
  "DialstackVoicemailBoxes",
  "AccountsReceivableReport",
  "Adjustments",
  "AdjustmentsQuickBooksSync",
  "Audiometers",
  "AppointmentCompletionReport",
  "AppointmentCounters",
  "AppointmentOutcomeReasons",
  "AppointmentStatuses",
  "AppointmentStatusesByReferralSource",
  "AppointmentCreatedByReferralSource",
  "AppointmentTimer",
  "AppointmentTypes",
  "Appointments",
  "AssignedAppointmentTags",
  "AssignedConversationTags",
  "AssignedPatientTags",
  "AssignedPatientProductTags",
  "AssignedSaleTags",
  "AssignedPatientChartNoteTags",
  "AssignedTouchpoints",
  "BulkTouchpoints",
  "TouchpointsPreview",
  "AudiometerConfigurations",
  "VerificationEquipment",
  "ChartNoteCounts",
  "ChartNotes",
  "CommissionPlanAssignments",
  "CommissionPlanRules",
  "CommissionPlans",
  "CommissionStatements",
  "CommissionsReport",
  "CPTCodes",
  "Credits",
  "ClaimsReport",
  "Clinics",
  "ClinicVideos",
  "InternalDocuments",
  "WebSchedulingConfig",
  "WebSchedulerConfigurations",
  "PatientTags",
  "PatientPortalVideos",
  "ClinicCampaigns",
  "CustomFilterViews",
  "CustomTests",
  "Dashboards",
  "EraClaims",
  "Paginated-EraClaims",
  "Events",
  "ExternalIntegrations",
  "ProductsAccounts",
  "FormTemplates",
  "Notifications",
  "NotificationUnreadCounts",
  "HCFA1500Forms",
  "ICD10Codes",
  "Implementation",
  "Invoices",
  "IntakeForms",
  "IntakeFormSubmissionsReport",
  "InsurerCptCodes",
  "PatientCptCodes",
  "Insurers",
  "InsurancePolicies",
  "InsuranceVerifications",
  "InsurerReceivables",
  "InsurerCreditApplications",
  // Balances and aging on the ledger report and Settings › Insurers; a manual adjustment moves both.
  "InsurerCreditBalances",
  "InventoryBundle",
  "InventoryBundles",
  "InventoryProducts",
  "InventoryProductDetail",
  "InventoryProductCounters",
  "Invites",
  "Faxes",
  "FaxesCount",
  "FinancialPeriodLocks",
  "ManagedCarePlans",
  "ManagedCareProviders",
  "ManagedCareReceivables",
  "MarketplaceIntegrations",
  "MasterProducts",
  "Manufacturers",
  "PracticeManufacturers",
  "MergeTagsPatient",
  "MergeTagsReport",
  "MergeTagsSale",
  "MergeTagsAppointment",
  "MergeTagsPatientProduct",
  "MergeTagsChartNote",
  "Messages",
  "AssignedDialogsCount",
  "MessageTemplates",
  "MFAAuditLog",
  "PracticeMarketplaceIntegrations",
  "NoahAppConnections",
  "NoahEventSubscriptions",
  "NoahPatient",
  "NoahSessions",
  "NoahDevices",
  "Orders",
  "OrderItems",
  "OnboardingFormRequest",
  "InOfficeFormSession",
  "IntakeFormValues",
  "PatientTags",
  "Patient",
  "PatientCards",
  "Patients",
  "Payments",
  "PaymentsQuickBooksSync",
  "PaymentsBreakdown",
  "PatientStaffTasks",
  "PatientNotes",
  "Physicians",
  // Separate from "Physicians" on purpose: every physician create/edit invalidates that tag, which
  // would refetch every expanded communication panel on the page for a change that cannot affect them.
  "PhysicianCommunications",
  "PatientDocuments",
  "PatientPortalAudiologyReports",
  "PatientPortalDocuments",
  "EDocuments",
  "NewEDocuments",
  "Products",
  "ProductCategories",
  "PatientProducts",
  "PatientProductsCounters",
  "PatientProductsWithAssignedTags",
  "PatientDialogs",
  "PatientFakeDialogs",
  "PatientQuickBooksDetails",
  "PatientSummary",
  "PriceTables",
  "PriceTableItems",
  "PriorAuthorizationCodes",
  "ProductsRequests",
  "PodiumToken",
  "PodiumDirectory",
  "PodiumClinicMappings",
  "PodiumUserMappings",
  "QuickBooksTokens",
  "QuickBooksProducts",
  "QuickBooksCompanies",
  "QuickBooksProductsRelations",
  "QuickBooksDefaultMappings",
  "QuickBooksAdjustmentTypesMappings",
  "QuickBooksAccounts",
  "QuickBooksRefundAccounts",
  "QuickBooksTaxProducts",
  "QuickBooksSyncExceptions",
  "QuickBooksClassMappings",
  "QuickBooksClassMappingRules",
  "QuickBooksSyncExclusionRules",
  "QuickBooksDashboard",
  "Rooms",
  "Refunds Report",
  "Sales Refunds",
  "ReferralSourceChildren",
  "ReferralSources",
  "RefundsQuickBooksSync",
  "Regions",
  "Teams",
  "MentionsForMe",
  "Report",
  "Reports",
  "ReportSummaryTemplates",
  "Surveys",
  "Schedule",
  "SearchProducts",
  "Sales",
  "SalesQuickBooksSync",
  "Paginated-Sales",
  "List-Paginated-Sales",
  "SaleItemCosts",
  "SaleItems",
  "SaleClaimEraNotes",
  "PaymentRequestNotes",
  "PaymentRequestCard",
  "SaleSourceBilling",
  "PatientSourceRecord",
  "PatientSourceDevices",
  "SourceEntityRecord",
  "SalesTaxes",
  "GeneralLedger",
  "StaffAvailability",
  "ScheduledEvents",
  "StaffTasks",
  "StaffTasksCount",
  "StaffTaskAttachments",
  "Staff",
  "StaffMetrics",
  "StaffNetPayments",
  "Statements",
  "SubReferralSources",
  "Tags",
  "HiddenGlobalTags",
  "TagActionTemplates",
  "TemplateCategories",
  "TerminalReaders",
  "MarketingDashboard",
  "CampaignEffectiveness",
  "Touchpoints",
  "CallBackEntries",
  "CallBackAttempts",
  "CallBackReasons",
  "TextTemplates",
  "TextRoutingCounts",
  "SmsDeliveryFailures",
  "UnansweredTexts",
  "UnreadMessagesCount",
  "Users",
  "Vendors",
  "WeeklySchedule",
  "WordLists",
  "PaymentRequests",
  "ClinicTaxes",
  "RegionTaxes",
  "DiscountTypes",
  "Form",
  "ReportAttachments",
  "AiInsights",
  "AiSalesForecastBands",
  "SurveyReports",
  "RoomTraffic",
  "SalesManagement",
  "SalesManagementAppointmentTypes",
  "SalesByZipCode",
  "SalesByAppointmentType",
  "MigrationRecords",
  "BrowsableEdocs",
  "VisitTrackers",
  "VisitTrackerReport",
  "VisitTrackersAppointments",
  "Prompt",
  "AmbientScribes",
  "AmbientScribeFeedback",
  "AmbientScribeTemplates",
  "AmbientScribeProcessingEvents",
  "AmbientScribeSessions",
  "AmbientScribeAudioChunks",
  "AmbientScribeNotes",
  "AmbientScribeAnalytics",
  "ScribeInsights",
  "InsightCategories",
  "InsightTags",
  // SN-19269. Three tags, not one: recording ids and follow-up ids share an id
  // space, so a single tag would make id-scoped tags impossible to add later
  // without collisions. A status PATCH must invalidate BOTH item tags.
  "FollowUpRecordings",
  "FollowUps",
  "FollowUpGenerations",
  "ConsultDebriefs",
  "GoogleCalendars",
  "GoogleCredentials",
  "SendbirdUsers",
  "PatientAlerts",
  "AlertsReport",
  "PaywallFeatures",
  "PaywallLicenses",
  "PaywallUserFeatures",
  "PaywallUserSubscriptions",
  "PaywallFeatureSubscriptions",
  "AmbientScribeTrialOffer",
  "AmbientScribePurchaseSummary",
  "Collections",
  "CollectionsSetup",
  "AmbientScribeSetup",
  "CoachSetup",
  "ChatSetup",
  "ChatFlags",
  "McpPracticeAccess",
  "McpWriteGrants",
  "McpConnections",
  "McpAuditEvents",
  "CollectionOutreach",
  "CollectionDisputes",
  "CollectionPaymentPlans",
  "CollectionCadenceTemplates",
  "CollectionBilling",
  "VobListing",
  "BulkActions",
  "CommsSetup",
  "ZoomConnection",
  "ZoomConnections",
  "ReferralSourceRules",
  "AppointmentTypeChecklistItems",
  "ActiveVisitChecklists",
  "AppointmentChecklist",
  "StaffRegions",
  "StaffLicenseDefaults",
  "StaffWebSchedulingDays",
  "GoogleReviews",
  "GoogleReviewConnections",
  "GoogleReviewSummary",
  "GoogleReviewsSetup",
  "GoogleReviewsWidgetSettings",
  "GoogleReviewsReplyKeywords",
  "ReviewRequests",
  "ReviewRequestsSummary",
  "PatientReviewRequestStatus",
  "PatientReviewRequestHistory",
  "ClinicStaffSchedule",
  "DashboardWidgets",
  "DashboardWidgetConfigs",
  "DashboardWidgetRegistry",
  "TrainingScenarios",
  "TrainingAssignments",
  "TrainingRuns",
  "TrainingProgress",
  // Sales Coach (SN-19517). Two lists, deliberately: `/coach/opportunity-appointments/`
  // is the worklist and `/coach/sales-reviews/` is the page of scored reviews. The
  // opportunities page reads BOTH (the review list fills the payer / disclosure-flag
  // columns the appointment row does not carry), so anything that changes a review's
  // state has to invalidate both. `/coach/playbook/` is deliberately untagged — a
  // published framework version is immutable.
  "SalesCoachOpportunities",
  "SalesCoachReviews",
  // Custom campaigns (`features/marketing/custom-campaigns/services/`). The
  // detail/list reads were already tagged `CustomCampaign` before the type was
  // registered here; `CustomCampaignVersions` is the History tab's list, keyed by
  // clinic-campaign id, invalidated by every version save.
  "CustomCampaign",
  "CustomCampaignVersions",
  // A regular campaign's versions (SN-20127). Same URL as `CustomCampaignVersions`, but
  // saved and refetched by the regular details page, never by the custom pages.
  "RegularCampaignVersions",
  "DirectMail"
] as const;

export const emptySplitApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauthFn(getDynamicApiUrl),
  tagTypes: API_TAGS,
  endpoints: () => ({})
});
